import type { IconName, IconProps } from "./Icon";
import { Icon } from "./Icon";
import type { Component } from "solid-js";
import { createMemo, mergeProps } from "solid-js";
import type { ClassList } from "../../types";
import { isEmpty } from "lodash-es";

type TileProps = {
  icon: IconName;
  iconClassList: ClassList;
  size: 20 | 24 | 32 | 40 | 44 | 52;
};
const Tile: Component<TileProps> = (props) => {
  const iconSize = createMemo<IconProps["size"]>(() => {
    switch (props.size) {
      case 20:
        return "sm";
      case 24:
        return "md";
      case 32:
        return "md";
      case 40:
        return "lg";
      case 44:
        return "xl";
      case 52:
        return "xl";
    }
  });

  return (
    <div
      class="inline-flex shrink-0 items-center justify-center rounded-xs"
      classList={{
        ...props.iconClassList,
        "size-5": props.size === 20,
        "size-6": props.size === 24,
        "size-8": props.size === 32,
        "size-10": props.size === 40,
        "size-11": props.size === 44,
        "size-13": props.size === 52,
      }}
    >
      <Icon name={props.icon} size={iconSize()} type="solid" />
    </div>
  );
};

export type IconTileColor =
  | "icon-tertiary"
  | "icon-brand"
  | "icon-theme-blue"
  | "icon-theme-green"
  | "icon-theme-purple"
  | "icon-theme-red"
  | "icon-theme-orange"
  | "icon-theme-pink"
  | "icon-theme-gray"
  | "icon-accent-yellow";

export type IconTileBgColor =
  | "bg-surface-extra-subtle"
  | "bg-accent-subtle-yellow"
  | "bg-accent-subtle-purple"
  | "bg-accent-brand-subtle";

type IconTileProps = {
  icon: IconName;
  size: "xs" | "sm" | "md" | "lg" | "xl";
  class?: string;
  color?: IconTileColor;
  bgColor?: IconTileBgColor;
  border?: boolean;
  disabled?: boolean;
};
export const IconTile: Component<IconTileProps> = (rawProps) => {
  const props = mergeProps({ border: false, disabled: false }, rawProps);
  const iconColor = createMemo(() => props.color ?? "icon-primary");
  const backgroundColor = createMemo(() => props.bgColor ?? "bg-surface-extra-subtle");
  const size = createMemo<TileProps["size"]>(() => {
    switch (props.size) {
      case "xs":
        return 24;
      case "sm":
        return 32;
      case "md":
        return 40;
      case "lg":
        return 44;
      case "xl":
        return 52;
    }
  });

  return (
    <Tile
      icon={props.icon}
      iconClassList={{
        [backgroundColor()]: true,
        "border border-subtle": props.border,
        [iconColor()]: !props.disabled,
        "icon-disabled": props.disabled,
        [props.class ?? ""]: !isEmpty(props.class),
      }}
      size={size()}
    />
  );
};

type DynamicValueIconTileProps = {
  icon: IconName;
  iconClassList: ClassList;
  size: "sm" | "md" | "lg";
};
export const DynamicValueIconTile: Component<DynamicValueIconTileProps> = (props) => {
  const size = createMemo<TileProps["size"]>(() => {
    switch (props.size) {
      case "sm":
        return 20;
      case "md":
        return 24;
      case "lg":
        return 40;
    }
  });

  return <Tile icon={props.icon} iconClassList={props.iconClassList} size={size()} />;
};
